import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import { IoEye, IoEyeOff } from 'react-icons/io5';

/*
 * ---------------------------------------------------------
 * FIREBASE
 * ---------------------------------------------------------
 *
 * Firebase web configuration values are public identifiers,
 * not private server secrets.
 *
 * We can move these into build environment variables later
 * for cleaner configuration management, but doing so does NOT
 * make them invisible in the compiled browser application.
 */
const firebaseConfig = {
    apiKey: 'AIzaSyDsN0pkNQgDoGmsEwcaUFbFVjJPdkVauSU',
    authDomain: 'oppies-f8112.firebaseapp.com',
    projectId: 'oppies-f8112',
    storageBucket: 'oppies-f8112.firebasestorage.app',
    messagingSenderId: '1098910112787',
    appId: '1:1098910112787:android:1eb0464baefa07cc3f33e6',
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/*
 * IMPORTANT:
 *
 * This Azure Function key IS sensitive.
 *
 * We are deliberately leaving the current endpoint untouched
 * during the visual redesign so existing portal login behaviour
 * remains unchanged.
 *
 * Next security upgrade:
 * Firebase ID token -> Azure Function -> token verification.
 */
const LOGIN_API =
    'https://oppiesfunc.azurewebsites.net/api/getShopByEmail?code=7KWINbztw9fLYDfl2dC2hQ-oOEljDDO170DmZm61jttOAzFuZHRDLw==';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const navigate = useNavigate();

    /*
     * ---------------------------------------------------------
     * EXISTING SAVED LOGIN BEHAVIOUR
     * ---------------------------------------------------------
     */
    useEffect(() => {
        const stored = localStorage.getItem('shopCredentials');

        if (!stored) {
            return;
        }

        try {
            const creds = JSON.parse(stored);

            if (creds?.email && creds?.password) {
                login(creds.email, creds.password, true);
            }
        } catch (error) {
            console.error('Unable to read stored shop credentials:', error);
            localStorage.removeItem('shopCredentials');
        }
    }, []);

    /*
     * ---------------------------------------------------------
     * LOGIN
     * ---------------------------------------------------------
     *
     * Existing authorization flow retained:
     *
     * 1. Firebase authenticates email/password.
     * 2. getShopByEmail retrieves shop from Cosmos.
     * 3. Cosmos document MUST contain:
     *
     *        "web": true
     *
     * 4. Shop details are placed in localStorage.
     * 5. User is taken to /dashboard.
     */
    const login = async (
        emailInput,
        passwordInput,
        isAutomaticLogin = false
    ) => {
        const cleanEmail = String(emailInput || '')
            .trim()
            .toLowerCase();

        if (!cleanEmail || !passwordInput) {
            setErrorMessage('Please enter your email address and password.');
            return;
        }

        try {
            setLoading(true);
            setErrorMessage('');

            // -------------------------------------------------
            // 1. FIREBASE AUTHENTICATION
            // -------------------------------------------------
            await signInWithEmailAndPassword(
                auth,
                cleanEmail,
                passwordInput
            );

            // -------------------------------------------------
            // 2. GET SHOP DOCUMENT
            // -------------------------------------------------
            const res = await axios.post(LOGIN_API, {
                email: cleanEmail,
            });

            // -------------------------------------------------
            // 3. PORTAL AUTHORIZATION
            // -------------------------------------------------
            //
            // IMPORTANT:
            // This remains the existing OPPIES Portal trigger.
            //
            // Cosmos:
            //
            //     "web": true
            //
            // must be a BOOLEAN true.
            //
            if (!res.data || res.data.web !== true) {
                setErrorMessage(
                    'This Op Shop does not currently have access to the Online Portal.'
                );

                return;
            }

            // -------------------------------------------------
            // 4. STORE SHOP DETAILS
            // -------------------------------------------------
            const {
                shopId,
                name,
                address,
                contact,
                email: shopEmail,
            } = res.data;

            localStorage.setItem('shopId', shopId || '');
            localStorage.setItem('shopName', name || '');
            localStorage.setItem('shopEmail', shopEmail || cleanEmail);
            localStorage.setItem('shopAddress', address || '');
            localStorage.setItem('shopContact', contact || '');

            // -------------------------------------------------
            // 5. DASHBOARD
            // -------------------------------------------------
            navigate('/dashboard');
        } catch (err) {
            console.error('Login error:', err);

            /*
             * If an automatically stored login has become invalid,
             * remove it rather than causing an endless login cycle.
             */
            if (isAutomaticLogin) {
                localStorage.removeItem('shopCredentials');
            }

            const firebaseCode = err?.code || '';

            if (
                firebaseCode === 'auth/invalid-credential' ||
                firebaseCode === 'auth/wrong-password' ||
                firebaseCode === 'auth/user-not-found'
            ) {
                setErrorMessage(
                    'The email address or password is incorrect.'
                );
            } else if (firebaseCode === 'auth/too-many-requests') {
                setErrorMessage(
                    'Too many login attempts. Please try again shortly.'
                );
            } else if (firebaseCode === 'auth/network-request-failed') {
                setErrorMessage(
                    'Unable to connect. Please check your internet connection and try again.'
                );
            } else {
                setErrorMessage(
                    'We could not sign you in. Please check your details and try again.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (loading) {
            return;
        }

        login(email, password);
    };

    return (
        <>
            <style>{`
                * {
                    box-sizing: border-box;
                }

                html,
                body,
                #root {
                    margin: 0;
                    min-height: 100%;
                }

                body {
                    margin: 0;
                }

                button,
                input {
                    font: inherit;
                }

                .oppies-login-page {
                    min-height: 100vh;
                    background: #ffffff;

                    color: #171717;

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Roboto,
                        Helvetica,
                        Arial,
                        sans-serif;

                    display: flex;
                    flex-direction: column;
                }

                /* ---------------------------------------------
                   TOP BAR
                   --------------------------------------------- */

                .oppies-topbar {
                    width: 100%;
                    border-bottom: 1px solid #f0f0f0;
                    background: rgba(255, 255, 255, 0.92);
                    backdrop-filter: blur(18px);
                    -webkit-backdrop-filter: blur(18px);
                }

                .oppies-topbar-inner {
                    width: min(1240px, calc(100% - 48px));
                    min-height: 86px;
                    margin: 0 auto;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 24px;
                }

                .oppies-logo {
                    display: block;
                    width: 185px;
                    max-width: 46vw;
                    height: auto;
                }

                .oppies-topbar-label {
                    color: #6b7280;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 0.01em;
                }

                /* ---------------------------------------------
                   MAIN
                   --------------------------------------------- */

                .oppies-main {
                    width: min(1240px, calc(100% - 48px));
                    flex: 1;
                    margin: 0 auto;

                    display: grid;
                    grid-template-columns:
                        minmax(0, 1.03fr)
                        minmax(380px, 0.72fr);

                    gap: clamp(56px, 8vw, 120px);
                    align-items: center;

                    padding: 70px 0 76px;
                }

                /* ---------------------------------------------
                   INTRO / HERO
                   --------------------------------------------- */

                .oppies-intro {
                    max-width: 680px;
                }

                .oppies-eyebrow {
                    display: inline-flex;
                    align-items: center;

                    margin-bottom: 25px;

                    color: #047857;

                    font-size: 12px;
                    line-height: 1;
                    font-weight: 700;

                    letter-spacing: 3px;
                    text-transform: uppercase;
                }

                .oppies-heading {
                    margin: 0;

                    max-width: 690px;

                    font-size: clamp(34px, 4vw, 52px);
                    line-height: 0.96;

                    font-weight: 650;
                    letter-spacing: -4.4px;
                }

                .oppies-heading-accent {
                    color: #047857;
                }

                .oppies-description {
                    margin: 30px 0 0;

                    max-width: 560px;

                    color: #60646c;

                    font-size: clamp(18px, 1.5vw, 21px);
                    line-height: 1.48;
                    font-weight: 400;
                }

                .oppies-feature-list {
                    margin: 36px 0 0;
                    padding: 0;

                    display: grid;
                    gap: 15px;

                    list-style: none;

                    color: #4b5563;
                    font-size: 15px;
                }

                .oppies-feature {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                }

                .oppies-dot {
                    width: 7px;
                    height: 7px;
                    flex: 0 0 7px;

                    border-radius: 999px;
                    background: #047857;
                }

                .oppies-support-note {
                    margin-top: 42px;
                    padding-top: 24px;

                    max-width: 530px;

                    border-top: 1px solid #ededed;

                    color: #8a8f98;
                    font-size: 13px;
                    line-height: 1.65;
                }

                /* ---------------------------------------------
                   LOGIN PANEL
                   --------------------------------------------- */

                .oppies-login-panel {
                    position: relative;

                    width: 100%;

                    padding: clamp(30px, 4vw, 46px);

                    overflow: hidden;

                    border: 1px solid #eee8db;
                    border-radius: 30px;

                    background: #fbf7ed;

                    box-shadow:
                        0 22px 60px rgba(27, 31, 35, 0.08),
                        0 3px 12px rgba(27, 31, 35, 0.03);
                }

                .oppies-login-panel::before {
                    content: "";

                    position: absolute;
                    top: -110px;
                    right: -100px;

                    width: 230px;
                    height: 230px;

                    border-radius: 50%;

                    background: #fbf7ed;

                    pointer-events: none;
                }

                .oppies-login-header {
                    position: relative;
                    z-index: 1;

                    margin-bottom: 32px;
                }

                .oppies-login-kicker {
                    margin-bottom: 10px;

                    color: #047857;

                    font-size: 11px;
                    font-weight: 700;

                    letter-spacing: 2.2px;
                    text-transform: uppercase;
                }

                .oppies-login-title {
                    margin: 0;

                    color: #171717;

                    font-size: 30px;
                    line-height: 1.1;

                    font-weight: 650;
                    letter-spacing: -1.4px;
                }

                .oppies-login-subtitle {
                    margin: 12px 0 0;

                    color: #737780;

                    font-size: 14px;
                    line-height: 1.55;
                }

                .oppies-form {
                    position: relative;
                    z-index: 1;
                }

                .oppies-field {
                    margin-bottom: 18px;
                }

                .oppies-label {
                    display: block;

                    margin-bottom: 8px;

                    color: #44484e;

                    font-size: 13px;
                    font-weight: 600;
                }

                .oppies-input {
                    width: 100%;
                    min-height: 52px;

                    padding: 0 16px;

                    outline: none;

                    border: 1px solid #dddcd7;
                    border-radius: 15px;

                    background: rgba(255, 255, 255, 0.93);

                    color: #171717;

                    font-size: 15px;

                    box-shadow:
                        0 1px 2px rgba(20, 20, 20, 0.02);

                    transition:
                        border-color 160ms ease,
                        box-shadow 160ms ease,
                        background 160ms ease;
                }

                .oppies-input::placeholder {
                    color: #a1a5ab;
                }

                .oppies-input:hover {
                    border-color: #cfd2cf;
                }

                .oppies-input:focus {
                    border-color: #047857;
                    background: #ffffff;

                    box-shadow:
                        0 0 0 4px rgba(4, 120, 87, 0.08);
                }

                .oppies-password-wrapper {
                    position: relative;
                }

                .oppies-password-input {
                    padding-right: 50px;
                }

                .oppies-password-toggle {
                    position: absolute;

                    top: 50%;
                    right: 11px;

                    width: 38px;
                    height: 38px;

                    transform: translateY(-50%);

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    padding: 0;

                    border: 0;
                    border-radius: 11px;

                    background: transparent;
                    color: #6b7280;

                    cursor: pointer;

                    transition:
                        color 150ms ease,
                        background 150ms ease;
                }

                .oppies-password-toggle:hover {
                    color: #047857;
                    background: #f3f7f5;
                }

                /* ---------------------------------------------
                   ERROR
                   --------------------------------------------- */

                .oppies-error {
                    margin: 4px 0 18px;
                    padding: 12px 14px;

                    border: 1px solid #fecaca;
                    border-radius: 13px;

                    background: #fff7f7;

                    color: #b42318;

                    font-size: 13px;
                    line-height: 1.45;
                }

                /* ---------------------------------------------
                   BUTTON
                   --------------------------------------------- */

                .oppies-login-button {
                    width: 100%;
                    min-height: 52px;

                    margin-top: 5px;
                    padding: 0 20px;

                    border: none;
                    border-radius: 15px;

                    background: #047857;
                    color: #ffffff;

                    font-size: 15px;
                    font-weight: 700;

                    cursor: pointer;

                    box-shadow:
                        0 7px 16px rgba(4, 120, 87, 0.17);

                    transition:
                        background 160ms ease,
                        transform 120ms ease,
                        box-shadow 160ms ease,
                        opacity 160ms ease;
                }

                .oppies-login-button:hover:not(:disabled) {
                    background: #065f46;

                    box-shadow:
                        0 9px 20px rgba(4, 120, 87, 0.22);
                }

                .oppies-login-button:active:not(:disabled) {
                    transform: scale(0.985);
                }

                .oppies-login-button:disabled {
                    opacity: 0.64;
                    cursor: default;
                }

                /* ---------------------------------------------
                   HELP
                   --------------------------------------------- */

                .oppies-help {
                    margin: 23px 0 0;

                    color: #85898f;

                    text-align: center;

                    font-size: 12.5px;
                    line-height: 1.55;
                }

                .oppies-help a {
                    color: #047857;
                    font-weight: 600;
                    text-decoration: none;
                }

                .oppies-help a:hover {
                    text-decoration: underline;
                }

                /* ---------------------------------------------
                   FOOTER
                   --------------------------------------------- */

                .oppies-footer {
                    width: 100%;

                    border-top: 1px solid #eeeeee;

                    background: #ffffff;
                }

                .oppies-footer-inner {
                    width: min(1240px, calc(100% - 48px));
                    min-height: 76px;

                    margin: 0 auto;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 24px;

                    color: #8a8f98;
                    font-size: 12px;
                }

                .oppies-footer a {
                    color: #047857;
                    text-decoration: none;
                    font-weight: 600;
                }

                .oppies-footer a:hover {
                    text-decoration: underline;
                }

                /* ---------------------------------------------
                   TABLET
                   --------------------------------------------- */

                @media (max-width: 900px) {
                    .oppies-main {
                        grid-template-columns: 1fr;
                        gap: 50px;

                        max-width: 680px;

                        padding-top: 55px;
                    }

                    .oppies-intro {
                        max-width: 620px;
                    }

                    .oppies-heading {
                        letter-spacing: -3.2px;
                    }

                    .oppies-support-note {
                        display: none;
                    }
                }

                /* ---------------------------------------------
                   MOBILE
                   --------------------------------------------- */

                @media (max-width: 600px) {
                    .oppies-topbar-inner,
                    .oppies-main,
                    .oppies-footer-inner {
                        width: min(100% - 32px, 1240px);
                    }

                    .oppies-topbar-inner {
                        min-height: 72px;
                    }

                    .oppies-logo {
                        width: 145px;
                    }

                    .oppies-topbar-label {
                        display: none;
                    }

                    .oppies-main {
                        gap: 34px;

                        padding-top: 42px;
                        padding-bottom: 48px;
                    }

                    .oppies-eyebrow {
                        margin-bottom: 18px;
                    }

                    .oppies-heading {
                        font-size: 39px;
                        letter-spacing: -2.5px;
                    }

                    .oppies-description {
                        margin-top: 22px;
                        font-size: 17px;
                    }

                    .oppies-feature-list {
                        margin-top: 28px;
                        font-size: 14px;
                    }

                    .oppies-login-panel {
                        padding: 28px 22px;
                        border-radius: 24px;
                    }

                    .oppies-login-title {
                        font-size: 27px;
                    }

                    .oppies-footer-inner {
                        min-height: auto;

                        padding: 23px 0;

                        flex-direction: column;
                        align-items: flex-start;

                        gap: 8px;
                    }
                }
            `}</style>

            <div className="oppies-login-page">
                {/* =====================================================
                    HEADER
                   ===================================================== */}
                <header className="oppies-topbar">
                    <div className="oppies-topbar-inner">
                        <a
                            href="https://www.oppies.nz"
                            aria-label="Visit OPPIES"
                        >
                            <img
                                src="/app_logo.png"
                                alt="OPPIES"
                                className="oppies-logo"
                            />
                        </a>

                        <div className="oppies-topbar-label">
                            OpShop Online Portal
                        </div>
                    </div>
                </header>

                {/* =====================================================
                    MAIN
                   ===================================================== */}
                <main className="oppies-main">

                    {/* -------------------------------------------------
                        LEFT / INTRO
                       ------------------------------------------------- */}
                    <section className="oppies-intro">
                        <div className="oppies-eyebrow">
                            For OpShops & Charities
                        </div>

                        <h1 className="oppies-heading">
                            Your OpShop.
                            <br />
                            Your community.
                            <br />
                            <span className="oppies-heading-accent">
                                All in one place.
                            </span>
                        </h1>

                        <p className="oppies-description">
                            Manage your OPPIES presence from any computer.
                            Keep your shop information fresh, connect with
                            local OpShoppers and make it easier for your
                            community to support the work you do. From $9 NZD per month.
                        </p>

                        <ul className="oppies-feature-list">
                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Manage your shop profile and opening hours
                            </li>

                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Create sales and keep your community informed
                            </li>

                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Update donations and volunteer information
                            </li>

                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Stay connected with the OPPIES community
                            </li>

                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Read reviews about what OpShoppers think about your Shop
                            </li>

                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Detailed statistics on Shop Profile Visits, Review count and more
                            </li>
                            <li className="oppies-feature">
                                <span className="oppies-dot" />
                                Manage multiple Shop Profiles
                            </li>
                        </ul>

                        <div className="oppies-support-note">
                            OPPIES is built to help charity and not-for-profit
                            OpShops connect with the people who support them,
                            while making secondhand shopping easier across
                            Australasia.
                        </div>
                    </section>

                    {/* -------------------------------------------------
                        RIGHT / LOGIN
                       ------------------------------------------------- */}
                    <section
                        className="oppies-login-panel"
                        aria-labelledby="portal-login-title"
                    >
                        <div className="oppies-login-header">
                            <div className="oppies-login-kicker">
                                Welcome Back
                            </div>

                            <h2
                                id="portal-login-title"
                                className="oppies-login-title"
                            >
                                OpShop Portal
                            </h2>

                            <p className="oppies-login-subtitle">
                                Sign in using your registered OPPIES
                                email address.
                            </p>
                        </div>

                        <form
                            className="oppies-form"
                            onSubmit={handleSubmit}
                        >
                            {/* EMAIL */}
                            <div className="oppies-field">
                                <label
                                    className="oppies-label"
                                    htmlFor="portal-email"
                                >
                                    Email Address
                                </label>

                                <input
                                    id="portal-email"
                                    className="oppies-input"
                                    type="email"
                                    placeholder="you@yourshop.org.nz"
                                    value={email}
                                    onChange={(event) => {
                                        setEmail(
                                            event.target.value.toLowerCase()
                                        );

                                        if (errorMessage) {
                                            setErrorMessage('');
                                        }
                                    }}
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    disabled={loading}
                                />
                            </div>

                            {/* PASSWORD */}
                            <div className="oppies-field">
                                <label
                                    className="oppies-label"
                                    htmlFor="portal-password"
                                >
                                    Password
                                </label>

                                <div className="oppies-password-wrapper">
                                    <input
                                        id="portal-password"
                                        className="oppies-input oppies-password-input"
                                        type={
                                            showPassword
                                                ? 'text'
                                                : 'password'
                                        }
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(event) => {
                                            setPassword(event.target.value);

                                            if (errorMessage) {
                                                setErrorMessage('');
                                            }
                                        }}
                                        autoComplete="current-password"
                                        disabled={loading}
                                    />

                                    <button
                                        type="button"
                                        className="oppies-password-toggle"
                                        onClick={() =>
                                            setShowPassword(
                                                (current) => !current
                                            )
                                        }
                                        aria-label={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                        title={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                    >
                                        {showPassword ? (
                                            <IoEyeOff size={20} />
                                        ) : (
                                            <IoEye size={20} />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* ERROR */}
                            {errorMessage && (
                                <div
                                    className="oppies-error"
                                    role="alert"
                                >
                                    {errorMessage}
                                </div>
                            )}

                            {/* LOGIN */}
                            <button
                                type="submit"
                                className="oppies-login-button"
                                disabled={loading}
                            >
                                {loading
                                    ? 'Signing in...'
                                    : 'Sign In to Portal'}
                            </button>

                            <p className="oppies-help">
                                Need help accessing your portal?
                                {' '}
                                <a href="mailto:support@oppies.nz">
                                    Contact the OPPIES team
                                </a>
                            </p>
                        </form>
                    </section>
                </main>

                {/* =====================================================
                    FOOTER
                   ===================================================== */}
                <footer className="oppies-footer">
                    <div className="oppies-footer-inner">
                        <div>
                            © {new Date().getFullYear()} OPPIES — an{' '}
                            <a
                                href="https://www.ecliptek.co.nz"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ECLIPTEK LTD
                            </a>{' '}
                            product. All rights reserved.
                        </div>

                        <div>
                            Built for the OpShop community.
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
}